import { lifecycleStatuses } from "@adva/types";
import type { CreateCustomerInput } from "@adva/types";
import {
  enumOptions,
  type EntityFieldConfig,
} from "@/components/v2/entity-form-config";

/**
 * ADV-787: shared field config for the customer create/edit pages. Labels and
 * enum options derive from the @adva/types CreateCustomerInputSchema so the
 * form stays in lockstep with the API contract. Minimal field set (identity +
 * lifecycle + lead source + notes); FK pickers (territory, assigned rep) are a
 * deliberate follow-up.
 */
export const CUSTOMER_FIELDS: EntityFieldConfig<CreateCustomerInput>[] = [
  { name: "first_name", label: "First name", control: "text" },
  { name: "last_name", label: "Last name", control: "text" },
  {
    name: "email",
    label: "Email",
    control: "email",
    placeholder: "name@example.com",
  },
  { name: "phone", label: "Phone", control: "tel" },
  { name: "company_name", label: "Company name", control: "text" },
  {
    name: "lifecycle_status",
    label: "Lifecycle status",
    control: "select",
    required: true,
    options: enumOptions(lifecycleStatuses),
  },
  {
    name: "customer_type",
    label: "Customer type",
    control: "select",
    emptyOptionLabel: "Unspecified",
    options: [
      { value: "residential", label: "Residential" },
      { value: "commercial", label: "Commercial" },
    ],
  },
  { name: "lead_source", label: "Lead source", control: "text" },
  { name: "notes", label: "Notes", control: "textarea" },
];

/** Map a loaded customer record onto the create-input-shaped form values. */
export function customerToFormValues(
  c: Record<string, unknown> | undefined,
): Partial<CreateCustomerInput> {
  if (!c) return {};
  const str = (v: unknown): string | undefined =>
    typeof v === "string" && v.length > 0 ? v : undefined;
  return {
    first_name: str(c.first_name),
    last_name: str(c.last_name),
    email: str(c.email),
    phone: str(c.phone),
    company_name: str(c.company_name),
    lifecycle_status:
      (str(c.lifecycle_status) as CreateCustomerInput["lifecycle_status"]) ??
      "lead",
    customer_type: c.customer_type as CreateCustomerInput["customer_type"],
    lead_source: str(c.lead_source),
    notes: str(c.notes),
  };
}
